import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';
import { EMPTY, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { UserInfo, UserInfoSchema } from './user-info';
import { UserInfoStore } from './user-info.store';
import { TranslateService } from '@ngx-translate/core';
import { getErrorMessage } from '../../../modules/shared/get-error-mesage';
import { SignalStoreCache } from '../../../modules/shared/signal-store';
import { decodeTolerantResult } from '../../../modules/shared/schema-validation';

const STORE_NAME = 'user-info';

@Injectable({ providedIn: 'root' })
export class UserInfoService {
  private infoStore = inject(UserInfoStore);
  private cache = inject(SignalStoreCache);

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  getUserInfo() {
    if (this.infoStore.loaded()) return EMPTY;
    // Always fetched from the API on page load (no IndexedDB cache), so admin changes show after a reload.
    this.infoStore.setLoading(true);
    return this.http.get<UserInfo>(`${environment.directoryApi}/user`).pipe(
      tap(x => {
        const { data } = decodeTolerantResult(UserInfoSchema, x, 'user-info.getUserInfo');
        this.infoStore.set(data);
      }),
      catchError(err => of(this.snackBar.open(getErrorMessage(err), 'OK', { panelClass: 'red-snackbar' }))),
      finalize(() => this.infoStore.setLoading(false))
    );
  }

  async refreshCache(): Promise<void> {
    // Invalidate IndexedDB first — once `loaded` drops, a concurrent
    // getUserInfo() may re-fetch and must not hit the stale cache entry
    await this.cache.invalidate(STORE_NAME);
    this.infoStore.resetCache();
  }

  async update(firstName: string, lastName: string, language: string) {
    try {
      const res = await this.http
        .patch<UserInfo>(
          `${environment.directoryApi}/user`,
          { first_name: firstName, last_name: lastName, language },
          { observe: 'response' }
        )
        .toPromise();

      if (res && res.status === 200 && res.body) {
        const { data } = decodeTolerantResult(UserInfoSchema, res.body, 'user-info.update');
        this.infoStore.updateData(data);
      }
    } catch (err) {
      this.snackBar.open(getErrorMessage(err), 'OK', { panelClass: 'red-snackbar' });
      throw err;
    }
  }

  async changePassword(oldPassword: string, newPassword: string) {
    try {
      await this.http
        .patch<any>(`${environment.directoryApi}/user`, {
          old_password: oldPassword,
          password: newPassword
        })
        .toPromise();

      this.snackBar.open(this.translate.instant('settings.password_successfully_changed'), 'OK', {
        panelClass: 'green-snackbar'
      });
    } catch (err) {
      let error = 'Something went wrong';
      if (err.status === 422) {
        error = this.translate.instant('settings.invalid_old_password');
      }
      this.snackBar.open(error, 'OK', { panelClass: 'red-snackbar' });
    }
  }
}
